import { Component, h, Element, Prop, Method, Event, EventEmitter, State, Host, Listen } from '@stencil/core';
import ArrowLarge from '../../../icons/arrow-large';

@Component({
  tag: 'zpr-showcase-preview-modal',
  styleUrl: 'showcase-preview-modal.scss'
})
export class ZprShowcasePreviewModal {
  private _modal: HTMLZprModalElement;

  @Element() el: HTMLElement;

  @Prop() hasFooterLink: boolean;
  @Prop() title: string;
  @Prop() itemId?: string;
  @Prop() description: string;
  @Prop() slug?: string;
  @Prop() wistiaVideoKey?: string;
  @Prop() youtubeVideoKey?: string;
  @Prop() vimeoVideoKey?: string;
  @Prop() prevItem?: any;
  @Prop() nextItem?: any;
  @Prop() hasCta:boolean = false;
  @Prop() ctaDisplayFrequency = 2;
  @Prop() ctaLinkUrl?: string;
  @Prop() ctaTitle?: string;
  @Prop() footerLink?: string;
  @Prop() heroImageUrl?: string;

  @State() isCtaVisible:boolean = false;
  @State() seenItemCount:number = 1;

  @Event() changed: EventEmitter;

  @Method()
  async open(showcaseItem: any, prevItem, nextItem) {
    this.updateModalContent(showcaseItem, prevItem, nextItem)
    this._modal.open()
  }

  @Method()
  async close() {
    this._modal.close()
  }

  @Listen('closed') handleClosed() {
    this.resetCta()
  }

  updateModalContent(item: any, prevItem, nextItem) {
    this.title = item.title
    this.itemId = item.id
    this.description = item.description
    this.slug = item.slug
    this.wistiaVideoKey = item.wistia_video_key
    this.youtubeVideoKey = item.youtube_video_key
    this.vimeoVideoKey = item.vimeo_video_key
    this.prevItem = prevItem
    this.nextItem = nextItem
    this.footerLink = item.external_link
    this.heroImageUrl = item.hero_image_url
  }

  handlePrevButton() {
    this.changed.emit({id: this.prevItem?.id, wistiaVideoKey: this.prevItem?.wistia_video_key})
    if (this.hasCta) {
      this.displayCta()
    }
  }

  handleNextButton() {
    this.changed.emit({id: this.nextItem?.id, wistiaVideoKey: this.nextItem?.wistia_video_key})
    if (this.hasCta) {
      this.displayCta()
    }
  }

  displayCta() {
    if(this.isCtaVisible) {
      this.isCtaVisible = false
    }
    if(this.seenItemCount === this.ctaDisplayFrequency) {
      this.isCtaVisible = true;
      this.seenItemCount = 0
    } else {
      this.seenItemCount++;
    }
  }

  resetCta() {
    this.isCtaVisible = false;
    this.seenItemCount = 1;
  }

  renderFooterLink() {
    if (this.isCtaVisible && this.ctaLinkUrl) {
      return <a href={this.ctaLinkUrl} class="btn btn-primary btn-small cta-btn">Find out more</a>
    } else if (this.hasFooterLink) {
      return <a href={this.footerLink ? this.footerLink : `/campaigns/${this.slug}/`} class="btn btn-secondary btn-small cta-btn" target={this.footerLink && '_blank'}>Find out more</a>
    }
  }

  render() {
    return (
    <Host class={this.isCtaVisible ? 'is-cta-visible': null}>
      <zpr-modal size="small" ref={el => this._modal = el}>
        <h3 slot="header">{ this.isCtaVisible ? this.ctaTitle : this.title }</h3>
        <div slot="body">
          <div class="modal-body">
            { this.wistiaVideoKey?.length ?
              <div class=" flex center mb12">
                <div class="w100 h100">
                  <div class="wistia_responsive_padding" style={{padding:'56.25% 0 0 0',position:'relative'}}>
                  <div class="wistia_responsive_wrapper" style={{height:'100%',left:'0',position:'absolute',top:'0',width:'100%'}}>
                  <iframe src={`https://fast.wistia.net/embed/iframe/${this.wistiaVideoKey}?seo=true&videoFoam=false`} data-src={`https://fast.wistia.net/embed/iframe/${this.wistiaVideoKey}?seo=true&videoFoam=false`}
                  title={ this.title } allow="autoplay; fullscreen" allowtransparency="true" frameborder="0" scrolling="no" class="wistia_embed rounded lazyload" name="wistia_embed" width="100%" height="100%"></iframe>

                  </div></div>
                </div>
              </div>
            : null}
            { this.youtubeVideoKey?.length ?
              <div class=" flex center mb12">
                <div class="w100 h100">
                  <div class="wistia_responsive_padding" style={{padding:'56.25% 0 0 0',position:'relative'}}>
                    <div class="wistia_responsive_wrapper" style={{height:'100%',left:'0',position:'absolute',top:'0',width:'100%'}}>
                      <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${this.youtubeVideoKey}`} title={this.title} frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullScreen></iframe>
                    </div>
                  </div>
                </div>
              </div>
            : null}
            { this.vimeoVideoKey?.length ?
              <div class=" flex center mb12">
                <div class="w100 h100">
                  <div class="wistia_responsive_padding" style={{padding:'56.25% 0 0 0',position:'relative'}}>
                    <div class="wistia_responsive_wrapper" style={{height:'100%',left:'0',position:'absolute',top:'0',width:'100%'}}>
                      <iframe width="100%" height="100%" src={`https://player.vimeo.com/video/${this.vimeoVideoKey}`} title={this.title} frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullScreen></iframe>
                    </div>
                  </div>
                </div>
              </div>
            : null}
            { !this.youtubeVideoKey?.length &&
              !this.wistiaVideoKey?.length &&
              !this.vimeoVideoKey?.length
            ? <div class=" flex center mb12">
              <img class="w100 hauto rounded" alt={this.title} src={this.heroImageUrl} />
            </div>
            : null}
            <div class="">
              <p class="text-left mb0">{this.description}</p>
            </div>
          </div>

          <slot name='cta-body'></slot>
        </div>

        <div slot="footer" class="w100">
          { this.prevItem ?
            <button class="btn btn-ghost prev-btn" onClick={() => this.handlePrevButton()}><ArrowLarge /> Back</button>
          : null}
          { this.renderFooterLink()}
          { this.nextItem ?
              <button class="btn btn-ghost next-btn" onClick={() => this.handleNextButton()}>Next <ArrowLarge /></button>
            : null}
        </div>
      </zpr-modal>
    </Host>)
  }
}
